import {
  AlertTriangle,
  CarFront,
  Construction,
  Cone,
  ShieldAlert,
  Snowflake,
  Wrench,
} from "lucide-react";
import type { MouseEvent } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { WazeAlert } from "@shared/types";

interface AlertMarkerProps {
  alert: WazeAlert;
  active?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  scale?: number;
}

type Style = {
  icon: LucideIcon;
  color: string;
  ring: string;
  label: string;
};

function styleFor(alert: WazeAlert): Style {
  switch (alert.type) {
    case "POLICE":
      return {
        icon: ShieldAlert,
        color: "bg-blue-500 text-white",
        ring: "ring-blue-300/60",
        label: "Police",
      };
    case "ACCIDENT":
      return {
        icon: CarFront,
        color: "bg-red-500 text-white",
        ring: "ring-red-300/60",
        label: "Accident",
      };
    case "HAZARD":
      if (alert.subtype === "HAZARD_WEATHER")
        return {
          icon: Snowflake,
          color: "bg-cyan-500 text-white",
          ring: "ring-cyan-300/60",
          label: "Weather",
        };
      return {
        icon: AlertTriangle,
        color: "bg-amber-500 text-black",
        ring: "ring-amber-200/60",
        label: "Hazard",
      };
    case "JAM":
      return {
        icon: Cone,
        color: "bg-orange-500 text-white",
        ring: "ring-orange-300/60",
        label: "Traffic",
      };
    case "CONSTRUCTION":
      return {
        icon: Construction,
        color: "bg-yellow-500 text-black",
        ring: "ring-yellow-200/60",
        label: "Construction",
      };
    case "ROAD_CLOSED":
      return {
        icon: Wrench,
        color: "bg-rose-600 text-white",
        ring: "ring-rose-300/60",
        label: "Road closed",
      };
    default:
      return {
        icon: AlertTriangle,
        color: "bg-zinc-500 text-white",
        ring: "ring-zinc-300/60",
        label: "Alert",
      };
  }
}

export function AlertMarker({
  alert,
  active,
  onClick,
  scale = 1,
}: AlertMarkerProps) {
  const style = styleFor(alert);
  const Icon = style.icon;
  const activeScale = active ? scale * 1.25 : scale;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={style.label}
      style={{ transform: `translateY(-50%) scale(${activeScale})` }}
      className={cn(
        "relative flex size-12 items-center justify-center rounded-full shadow-xl ring-4 ring-offset-2 ring-offset-background/0 transition-transform",
        style.color,
        style.ring,
        active && "z-10",
      )}
    >
      <Icon className="size-6" />
    </button>
  );
}

export const alertStyle = styleFor;
